/**
 * Tech one-tap flag — "Flag a problem" (field half of the Signal loop, #35).
 *
 * Field techs never type into a chat, so friction surfaces by word of mouth
 * or not at all. This is ONE button on the job screens: tap → four big chips
 * → Send. Zero required typing. The flag goes through the save-outbox (the
 * #36 honesty upgrades apply), so it works with no signal in a mechanical
 * room, and lands as a signal_interactions row the office sees clustered in
 * Rising Patterns.
 */
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Flag, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { saveThroughOutbox } from "@/lib/save-outbox";
import { appVersion } from "@/lib/app-version";
import { cn } from "@/lib/utils";

export const FLAG_CHIPS = [
    { key: "broken", label: "Something's broken" },
    { key: "cant_find", label: "Can't find something" },
    { key: "blocked", label: "App won't let me" },
    { key: "other", label: "Other" },
] as const;

export type FlagCategory = (typeof FLAG_CHIPS)[number]["key"];

interface FlagProblemButtonProps {
    /** Job the tech is standing on; null = app-wide gripe. */
    jobId: string | null;
    assetId?: string | null;
    className?: string;
    /** The button's word. The iPad home screen calls it "Help Desk" (#1838). */
    label?: string;
    /** Mark it as the tech's Help Desk, for the teach-cards spotlight (#1838). */
    teach?: boolean;
    /** e.g. "hidden sm:inline" — icon only on a phone, the word from tablet up. */
    labelClassName?: string;
}

export function FlagProblemButton({
    jobId,
    assetId = null,
    className,
    label = "Flag a problem",
    teach = false,
    labelClassName,
}: FlagProblemButtonProps) {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState<FlagCategory | null>(null);
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);

    function close() {
        setOpen(false);
        setCategory(null);
        setNote("");
    }

    async function send() {
        if (!category || sending) return;
        setSending(true);
        try {
            // Outbox-first: queued offline is still a success — the drain
            // sends it when signal comes back, so the toast is honest.
            await saveThroughOutbox({
                url: "/api/field/flag",
                method: "POST",
                body: {
                    jobId,
                    assetId,
                    category,
                    note: note.trim() || null,
                    route: location.pathname,
                    appVersion: appVersion(),
                },
            });
            toast.success("Flagged — the office will see the pattern.");
            close();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Couldn't send the flag — try again.");
        } finally {
            setSending(false);
        }
    }

    return (
        <>
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn("h-9 gap-1.5 text-shell-foreground/70 hover:text-shell-foreground", className)}
                onClick={() => setOpen(true)}
                data-teach={teach ? "helpdesk" : undefined}
                aria-label={label}
            >
                <Flag className="h-4 w-4" />
                <span className={labelClassName}>{label}</span>
            </Button>

            <Dialog
                open={open}
                onClose={close}
                title="Flag a problem"
                // Bottom-sheet feel on the phone: pin the native dialog to the
                // bottom edge instead of centering it.
                className="mx-auto mb-0 mt-auto w-full max-w-lg rounded-b-none"
            >
                <div className="space-y-4">
                    <p className="text-sm text-foreground/70">
                        One tap is enough — the office sees the pattern, not just this flag.
                    </p>
                    <div className="grid grid-cols-2 gap-2" role="group" aria-label="What went wrong?">
                        {FLAG_CHIPS.map((chip) => (
                            <button
                                key={chip.key}
                                type="button"
                                aria-pressed={category === chip.key}
                                onClick={() => setCategory(chip.key)}
                                className={cn(
                                    "h-14 rounded-xl border px-3 text-sm font-semibold leading-tight transition-colors",
                                    category === chip.key
                                        ? "border-primary bg-primary/15 text-primary"
                                        : "border-border bg-background text-foreground/90 hover:bg-secondary",
                                )}
                            >
                                {chip.label}
                            </button>
                        ))}
                    </div>
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value.slice(0, 500))}
                        placeholder="Add a note (optional)"
                        rows={2}
                        aria-label="Note (optional)"
                    />
                    <Button
                        type="button"
                        className="w-full h-12 text-base gap-2"
                        disabled={!category || sending}
                        onClick={send}
                    >
                        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                        Send
                    </Button>
                </div>
            </Dialog>
        </>
    );
}
